import { Alert, Box, Stack, Typography } from '@mui/material';
import type { GridInitialState } from '@mui/x-data-grid-premium';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useContext, useMemo } from 'react';

import { UserContext } from '~/auth/userContext';
import { displayDate, formatCurrency } from '~/components/contractReview/contractDetailShared';
import type { ContractReview, ContractReviewProperty } from '~/components/contractReview/types';
import { PropertyPreviewPanel } from '~/components/portfolio/PropertyPreviewPanel';
import { openInNewTab } from '~/components/rowPreview/RowPreviewPanel';
import { useTableRowPreview } from '~/components/rowPreview/useTableRowPreview';
import { LiftAITable } from '~/components/table';
import type { LiftAIGridColDef } from '~/components/table/colDefTemplate';
import { LiftAITextLink } from '~/components/utils/helpers';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { canAccess, NAVIGATE_TO_PROPERTY_PAGE } from '~/utils/role';
import { buildUrlForProperty } from '~/utils/urlBuilder';

const initialState: GridInitialState = {
  sorting: { sortModel: [{ field: 'monthlyPrice', sort: 'desc' }] },
  // The totals the old footer row carried, through the grid's own aggregation so they
  // follow the filter instead of always answering for the whole contract.
  aggregation: { model: { carCount: 'sum', monthlyPrice: 'sum' } },
};

/**
 * How the contract splits across the buildings it covers, on the shared grid the rest of
 * the app uses — a portfolio agreement runs to dozens of properties.
 */
export function ContractPropertiesTab({ contract }: { contract: ContractReview }) {
  const tableHeight = useDetailTableHeight();
  const apiRef = useGridApiRef();
  const getPropertyId = (rowId: string) =>
    apiRef.current?.getRow<ContractReviewProperty>(rowId)?.id ?? null;
  const { currencyCode, userDetails } = useContext(UserContext);
  const preview = useTableRowPreview({
    list: 'contract_properties',
    apiRef,
    canPreview: (rowId) => getPropertyId(rowId) !== null,
    // The same gate the Properties list puts on its preview.
    enabled: canAccess(userDetails.role, NAVIGATE_TO_PROPERTY_PAGE),
  });
  const terms = useLocaleTerms();

  const columns = useMemo<LiftAIGridColDef<ContractReviewProperty>[]>(() => {
    const rateSetNameById = new Map(contract.rateSets.map((set) => [set.id, set.name]));

    return [
      {
        field: 'name',
        headerName: 'Property',
        flex: 2,
        minWidth: 240,
        renderCell: ({ row }) => (
          <Stack>
            {/* A new tab: the property is opened alongside the contract, not instead of it. */}
            <LiftAITextLink
              label={row.name}
              href={buildUrlForProperty(row.id)}
              target="_blank"
              rel="noopener"
              variant="body2"
            />
            <Typography variant="caption" color="text.secondary" noWrap>
              {row.address}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'carCount',
        headerName: terms.vehicles,
        type: 'number',
        width: 90,
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'monthlyPrice',
        headerName: 'Monthly',
        type: 'number',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number) => formatCurrency({ value, currencyCode }),
      },
      {
        field: 'rateSet',
        headerName: 'Rate set',
        flex: 1,
        minWidth: 160,
        // 'None' rather than an em dash, which reads as a value we failed to load.
        valueGetter: (_, row) =>
          row.rateSetId === null ? 'None' : (rateSetNameById.get(row.rateSetId) ?? '—'),
      },
      {
        field: 'startDate',
        headerName: 'Term',
        flex: 1,
        minWidth: 200,
        filterable: false,
        renderCell: ({ row }) => (
          <Typography variant="body2" color="text.secondary">
            {displayDate(row.startDate)} → {displayDate(row.expirationDate)}
          </Typography>
        ),
      },
    ];
  }, [contract.rateSets, currencyCode, terms.vehicles]);

  if (contract.properties.length === 0) {
    return (
      <Box sx={{ p: 3, width: '100%' }}>
        <Alert severity="info">No properties on this contract.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <LiftAITable<ContractReviewProperty>
        height={tableHeight}
        apiRef={apiRef}
        rows={contract.properties}
        getRowId={(row) => row.propertyContractId}
        columns={columns}
        tableHeaderTitle="Properties"
        enableQuickFilter
        quickFilterPlaceHolder="Search properties"
        initialState={initialState}
        disableAggregation={false}
        toolbarProps={{ filterControls: preview.peekSwitch }}
        {...preview.tableProps}
      />
      {preview.enabled ? (
        <PropertyPreviewPanel
          rowPreview={preview.rowPreview}
          getPropertyId={getPropertyId}
          onOpenFullPage={(propertyId) => openInNewTab(buildUrlForProperty(propertyId))}
        />
      ) : null}
    </Box>
  );
}
